'use client';

import * as React from 'react';
import { Search, Store } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useStoresSearch } from '@/lib/api/stores';

interface StoreResourcePickerProps {
  value: string;
  onChange: (storeId: string) => void;
}

export function StoreResourcePicker({ value, onChange }: StoreResourcePickerProps) {
  const [searchQuery, setSearchQuery] = React.useState('');

  const { data: storesData, isLoading } = useStoresSearch({
    search: searchQuery || undefined,
    limit: 10,
  });

  const stores = storesData?.stores || [];

  return (
    <div className="w-full space-y-3">
      <div className="relative">
        <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        <Input
          type="text"
          placeholder="Search stores by name or ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-9 pl-9"
        />
      </div>

      <div className="max-h-[300px] w-full space-y-2 overflow-y-auto p-1">
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-card rounded-md border p-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : stores.length === 0 ? (
          <Card className="p-6">
            <div className="flex flex-col items-center justify-center text-center">
              <Store className="text-muted-foreground mb-2 h-8 w-8" />
              <p className="text-sm font-medium">No stores found</p>
              <p className="text-muted-foreground mt-1 text-xs">
                {searchQuery ? 'Try adjusting your search' : 'No stores available'}
              </p>
            </div>
          </Card>
        ) : (
          stores.map((store: any) => (
            <button
              key={store.id}
              type="button"
              onClick={() => onChange(store.id)}
              className={`bg-card hover:bg-accent w-full rounded-md border p-3 text-left transition-colors ${
                value === store.id ? 'ring-primary bg-accent ring-2' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="bg-muted flex h-10 w-10 flex-shrink-0 items-center justify-center rounded">
                  <Store className="text-muted-foreground h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="truncate text-sm font-medium">{store.name}</p>
                  <p className="text-muted-foreground truncate font-mono text-xs">{store.id}</p>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
